/* eslint-disable class-methods-use-this */
import $ from 'zepto';
import {
  tooltipTriggerSelector,
  tooltipContentClass,
  tooltipBodyClass,
  tooltipBodySelector,
  tooltipCloseClass,
  tooltipPointerClass,
  tooltipContentSelector,
} from '../../utils/constants/classes';
import { debounce } from '../../utils';
import { getHtml, safeGet } from '../../utils/ajax';
import { ESC_KEYCODE, TAB_KEYCODE } from '../../utils/constants';

export default class Tooltip {
  constructor() {
    this.tooltipSize = {
      small: 250,
      medium: 450,
      'max-content': 'max-content',
    };

    this.onClick = this.onClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.closeTooltip = this.closeTooltip.bind(this);
  }

  /**
   * Set the html structure and events of the tooltip
   *
   * @param {(HTMLElement|Zepto)} $tooltip - tooltip component
   * @param {String} id - tooltip id
   */
  init($tooltip, id) {
    const $trigger = $($tooltip.find(tooltipTriggerSelector));
    const closeType = $tooltip.attr('data-close-type');
    const size = $tooltip.attr('data-size') || 'medium';
    const $pointerDiv = $(`<div class="${tooltipPointerClass}"></div>`);

    const $content = this.getTooltipContent(id, $tooltip, $trigger);
    const $closeDiv = closeType && $(`<div class="${tooltipCloseClass}"></div>`);

    $content.append($closeDiv);
    $content.append($pointerDiv);

    $trigger.on('click focus', (event) => {
      event.preventDefault();
      $content.css('display', 'block');
      $content.width(this.tooltipSize[size]);
      this.positionContent($content, $trigger, $pointerDiv);
      event.stopPropagation();
    });

    this.setCloseEvents($content, closeType, $closeDiv);

    const onResize = debounce(() => {
      $content.width(this.tooltipSize[size]);
      this.positionContent($content, $trigger, $pointerDiv);
      $(window).on('scroll', { $content }, this.closeTooltip);
    }, 250);

    $(window).on('resize', () => {
      $(window).off('scroll');
      onResize();
    });

    return this;
  }

  /**
   * Create content of tooltip according his type
   *
   * @param {String} id - posible tooltip id
   * @param {(HTMLElement|Zepto)} $tooltip - tooltip element
   * @param {(HTMLElement|Zepto)} $trigger - tooltip trigger element
   * @returns {(HTMLElement|Zepto)}
   */
  getTooltipContent(id, $tooltip, $trigger) {
    const type = $tooltip.attr('data-type') || 'no-ajax';
    const $content = $(`<div class="${tooltipContentClass}" role="tooltip"></div>`);

    if (type === 'ajax') {
      const href = $trigger.attr('href');
      const $body = $(`<div id="${id}"></div>`);
      $tooltip.append($content);
      $content.append($body);
      $trigger.attr('aria-describedby', id);
      $trigger.on('click focus', () => {
        getHtml(href, safeGet(href, (result) => {
          $body.html(result);
        }));
      });
    } else {
      $tooltip.append($content);

      const $body = $tooltip.find(tooltipBodySelector);
      $body.removeClass(tooltipBodyClass);
      $body.appendTo($content);
      const bodyId = $body.attr('id');

      if (bodyId) {
        $trigger.attr('aria-describedby', bodyId);
      } else {
        $trigger.attr('aria-describedby', id);
        $body.attr('id', id);
      }
    }
    return $content;
  }

  /**
   * Add close events for tooltip
   *
   * @param {(HTMLElement|Zepto)} $content - tooltip content element
   * @param {String} closeType - can be button or undefined
   * @param {(HTMLElement|Zepto)} $closeDiv - close button element
   */
  setCloseEvents($content, closeType, $closeDiv) {
    if (closeType === 'button') {
      $closeDiv.on('click', { $content }, this.closeTooltip);
    }
    $(window).on('scroll', { $content }, this.closeTooltip);
    $(window).on('click', { $content }, this.onClick);
    $(window).on('keydown', { $content }, this.onKeyDown);
  }

  onClick(event) {
    if (!$(event.target).closest(tooltipContentSelector).get(0)
      && !$(event.target).closest(tooltipTriggerSelector).get(0)) {
      event.data.$content.css('display', 'none');
    }
  }

  onKeyDown(event) {
    if (event.which === ESC_KEYCODE || event.which === TAB_KEYCODE) {
      event.data.$content.css('display', 'none');
    }
  }

  closeTooltip(event) {
    event.preventDefault();
    if (event.data.$content.css('display') !== 'none') {
      event.data.$content.css('display', 'none');
    }
    event.stopPropagation();
  }

  /**
   * Positioning of tooltip based on getBoundingClientRect() of his elements
   *
   * @param {(HTMLElement|Zepto)} $content - tooltip content element
   * @param {(HTMLElement|Zepto)} $trigger - tooltip trigger element
   * @param {(HTMLElement|Zepto)} $pointerDiv - pointer element
   * @returns {(HTMLElement|Zepto)}
   */
  positionContent($content, $trigger, $pointerDiv) {
    const windowWidth = window.innerWidth;
    const edgeSpacing = 12;
    const contentTopSpacing = 5;
    const offsetPointer = 3;

    if ($content.width() + edgeSpacing * 2 > windowWidth) {
      $content.width(windowWidth - edgeSpacing * 2);
    }

    const boundingTrigger = $trigger[0].getBoundingClientRect();
    const { left: leftTrigger, top: topTrigger } = boundingTrigger;
    let leftContent = leftTrigger + ($trigger.width() - $content.width() + $pointerDiv.width()) / 2;

    $content.css('left', leftContent);
    const topContent = topTrigger + $trigger.height() + contentTopSpacing;
    $content.css('top', topContent);
    $pointerDiv.css('top', topContent - ($pointerDiv.height() / 2) - offsetPointer);

    let boundingContent = $content[0].getBoundingClientRect();
    const { left } = boundingContent;
    const inLeftEdge = (left - edgeSpacing) > 0 && (left + edgeSpacing) < windowWidth;

    if (!inLeftEdge) {
      leftContent = leftTrigger - edgeSpacing;
      $content.css('left', leftContent);
    }

    boundingContent = $content[0].getBoundingClientRect();
    const { right } = boundingContent;
    const inRightEdge = (right - edgeSpacing) > 0 && (right + edgeSpacing) < windowWidth;

    if (!inRightEdge) {
      leftContent = leftContent + windowWidth - right - edgeSpacing;
      $content.css('left', leftContent);
    }

    const contentPadding = parseInt($content.css('padding-right'), 10);
    const leftPointerPosition = leftTrigger + ($trigger.width() / 2)
      + ($pointerDiv.width() / 2) - contentPadding - offsetPointer;

    $pointerDiv.css('left', leftPointerPosition);
  }
}
